// redux/user/publicProfile/userPublicProfileReducer.js

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPublicProfile,
  searchPublicUsers,
} from "./userPublicProfileThunks";

/**
 * State shape:
 * {
 *   loading: boolean,
 *   user: PublicUserResponse | null,
 *   error: string | null,
 *   lastFetchedAt: number | null,
 *   search: {
 *     loading: boolean,
 *     results: Array<any>,
 *     error: string | null
 *   }
 * }
 */

const initialState = {
  loading: false,
  user: null,
  error: null,
  lastFetchedAt: null,
  search: {
    loading: false,
    results: [],
    error: null,
  },
};

const publicProfileSlice = createSlice({
  name: "profilepublic",
  initialState,
  reducers: {
    clearPublicProfile(state) {
      state.user = null;
      state.error = null;
      state.lastFetchedAt = null;
    },
    resetPublicProfileErrors(state) {
      state.error = null;
      state.search.error = null;
    },
    // Optional: allow hydration during SSR or route prefetch
    setPublicProfile(state, action) {
      state.user = action.payload || null;
      state.error = null;
      state.lastFetchedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    // ------- fetchPublicProfile -------
    builder.addCase(fetchPublicProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPublicProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload || null;
      state.error = null;
      state.lastFetchedAt = Date.now();
    });
    builder.addCase(fetchPublicProfile.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : action.error?.message || "Failed to load public profile";
    });

    // ------- searchPublicUsers -------
    builder.addCase(searchPublicUsers.pending, (state) => {
      state.search.loading = true;
      state.search.error = null;
      state.search.results = [];
    });
    builder.addCase(searchPublicUsers.fulfilled, (state, action) => {
      state.search.loading = false;
      state.search.results = Array.isArray(action.payload)
        ? action.payload
        : [];
      state.search.error = null;
    });
    builder.addCase(searchPublicUsers.rejected, (state, action) => {
      state.search.loading = false;
      state.search.results = [];
      state.search.error =
        typeof action.payload === "string"
          ? action.payload
          : action.error?.message || "Failed to search users";
    });
  },
});

export const {
  clearPublicProfile,
  resetPublicProfileErrors,
  setPublicProfile,
} = publicProfileSlice.actions;

export default publicProfileSlice.reducer;
